/**
 * The four boxes ⇄ one stored document.
 *
 * THE PLAN, §4 P3: the owner edits four labelled fields — who it is, what the business
 * does, what it must always do, what it must never do — and what the assistant actually
 * reads is one document. This module is the whole of that translation, kept out of the
 * components so the round-trip can be reasoned about (and tested) on its own.
 *
 * THREE PROPERTIES IT MUST HAVE, in order of importance:
 *
 *   1. NOTHING HAND-WRITTEN IS EVER LOST. `parseSections` is deliberately STRICT: it
 *      returns null for any document it did not itself produce. A null puts the page into
 *      the single-document editor, which shows the text byte-for-byte. There is no
 *      "close enough" branch on the read path, because a close-enough split silently
 *      moves a sentence from one box to another and the owner finds out weeks later,
 *      from a customer.
 *
 *   2. compose(parse(x)) === x for anything we wrote. The markers are fixed ASCII
 *      headings, NOT the translated labels — an account that switches to Arabic must not
 *      stop recognising its own document. The owner never sees them; only the model does.
 *
 *   3. A body can never fake a marker. `compose` indents any body line that would read
 *      back as a heading. One leading space is invisible to the model and to markdown,
 *      and it makes the split deterministic instead of "almost always right".
 *
 * `parseLoosely` is the OPPOSITE trade and exists for exactly one caller: the
 * preview-then-accept conversion, where the text on screen is a fresh suggestion the
 * owner is about to read in full and explicitly accept or throw away. Guessing is safe
 * there and nowhere else.
 */

/** Canonical order. Every consumer iterates this, so the boxes and the document agree. */
export const SECTION_KEYS = ['identity', 'business', 'always', 'never'];

/**
 * Fixed, untranslated markers. Changing one of these strings orphans every document
 * already saved by every account — treat them as a storage format, not as copy.
 */
const MARKERS = {
  identity: '## Who it is',
  business: '## What the business does',
  always: '## What it must always do',
  never: '## What it must never do',
};

const MARKER_LINES = new Set(Object.values(MARKERS));

const emptySections = () =>
  SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

/** Property 3: a body line that looks like a marker is pushed one space out of the way. */
const shieldBody = body =>
  body
    .split('\n')
    .map(line => (MARKER_LINES.has(line.trimEnd()) ? ` ${line}` : line))
    .join('\n');

/**
 * @param {Record<string, string>} sections
 * @returns {string} The document the assistant reads. Empty boxes are omitted entirely,
 *   so an owner who only filled two of four does not ship two empty headings.
 */
export const composeSections = (sections = {}) =>
  SECTION_KEYS.map(key => {
    const body = (sections[key] ?? '').trim();
    return body ? `${MARKERS[key]}\n\n${shieldBody(body)}` : '';
  })
    .filter(Boolean)
    .join('\n\n');

/**
 * @param {string} document
 * @returns {Record<string, string> | null} The four boxes, or null when this document did
 *   not come out of `composeSections` — which is the signal to open the single-document
 *   editor rather than to guess.
 */
export const parseSections = document => {
  if (typeof document !== 'string') return null;
  const text = document.trim();
  if (!text) return null;

  const found = [];
  let strayPreamble = false;

  text.split('\n').forEach(line => {
    const marker = line.trimEnd();
    if (MARKER_LINES.has(marker)) {
      const key = SECTION_KEYS.find(candidate => MARKERS[candidate] === marker);
      found.push({ key, body: [] });
      return;
    }
    // Anything before the first marker means this is somebody's own document.
    if (!found.length) {
      if (line.trim()) strayPreamble = true;
      return;
    }
    found[found.length - 1].body.push(line);
  });

  if (strayPreamble || !found.length) return null;

  // Strictly increasing rules out both a repeated marker and a shuffled one, which is
  // what tells "our document" apart from "a document that happens to quote our headings".
  const order = found.map(entry => SECTION_KEYS.indexOf(entry.key));
  const canonical = order.every(
    (index, position) => position === 0 || index > order[position - 1]
  );
  if (!canonical) return null;

  return found.reduce(
    (sections, { key, body }) => ({
      ...sections,
      [key]: body.join('\n').trim(),
    }),
    emptySections()
  );
};

/** Convenience: does this document round-trip into the four boxes? */
export const isSectioned = document => parseSections(document) !== null;

// ── the guessing half, for the conversion preview only ─────────────────────────────

const BUCKETS = [
  // `never` is tested before `always` on purpose: "what it must never do" contains "must".
  { key: 'never', pattern: /never|not\s+do|avoid|forbidden|prohibit|don'?t/i },
  { key: 'always', pattern: /always|must|rule|guideline|do\b|behaviou?r/i },
  {
    key: 'business',
    pattern:
      /business|company|shop|store|about\s+us|product|service|we\s+(are|sell|offer)/i,
  },
  {
    key: 'identity',
    pattern: /who|you\s+are|role|identity|assistant|name|tone|voice/i,
  },
];

const bucketFor = heading =>
  BUCKETS.find(b => b.pattern.test(heading))?.key ?? null;

/**
 * Best-effort split of a document we did NOT write, for the conversion preview.
 * Never called on load. Returns null when there is nothing headed to work with, so the
 * caller can tell the owner honestly that it could not be split.
 *
 * @param {string} document
 * @returns {Record<string, string> | null}
 */
export const parseLoosely = document => {
  if (typeof document !== 'string' || !document.trim()) return null;

  const strict = parseSections(document);
  if (strict) return strict;

  const sections = emptySections();
  const preamble = [];
  let currentKey = null;
  let buffer = [];
  let matched = 0;

  const flush = () => {
    if (!currentKey) return;
    const body = buffer.join('\n').trim();
    if (body) {
      sections[currentKey] = sections[currentKey]
        ? `${sections[currentKey]}\n${body}`
        : body;
    }
    buffer = [];
  };

  document.split('\n').forEach(line => {
    const heading = /^\s{0,3}#{1,4}\s+(.*\S)\s*$/.exec(line);
    if (heading) {
      flush();
      currentKey = bucketFor(heading[1]);
      if (currentKey) matched += 1;
      return;
    }
    if (currentKey) buffer.push(line);
    else preamble.push(line);
  });
  flush();

  if (!matched) return null;

  const intro = preamble.join('\n').trim();
  if (intro) {
    sections.identity = sections.identity
      ? `${intro}\n\n${sections.identity}`
      : intro;
  }
  return sections;
};
